import React, { useState, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { DataGrid } from '@weave-mui/data-grid';
import Typography from '@weave-mui/typography';
import {
  Box,
  IconButton,
  TextField,
  InputAdornment,
  Link,
  Tooltip,
} from '@weave-mui/material';
import Button, { buttonVariants, buttonTargets } from '@weave-mui/button';

import { Search as SearchIcon, Add as AddIcon } from '@mui/icons-material';
import { CopyM as CopyIcon } from '@weave-mui/icons-weave';

import LoadingSpinner from 'components/LoadingSpinner';
import ShareTestSuiteModal from 'components/ShareTestSuiteModal';
import ConfirmationModal from 'components/ConfirmationModal';
import TestSuiteDetailsDrawer from 'components/TestSuiteDetailsDrawer';
import ScheduleModal from 'containers/TestSuites/ScheduleModal';
import ModalRenderer from 'components/Modal/ModalRenderer';
import { useModal } from 'hooks';
import { MODAL_IDS } from 'config/modalConfigs';
import ScheduleDisplay from 'components/ScheduleDisplay';
import {
  removeTestSuite,
  shareTestSuite,
} from 'store/actions/test_suites_actions';

/**
 * TestSuitesDataGrid - A comprehensive data grid component for displaying test suites
 * Provides functionality for viewing, running, scheduling, and managing test suites with
 * support for search, pagination, and various user actions.
 *
 * @param {Object} props - Component props
 * @param {Array} props.suites - Array of test suite objects organized by service (required)
 * @param {Array} props.roles - Array of available user roles for permissions (required)
 * @param {Object} props.userInfo - Current user information object (required)
 * @param {boolean} [props.loadingTestSuites=false] - Whether test suites are currently loading
 * @returns {JSX.Element} The rendered test suites data grid component
 *
 * @example
 * // Basic usage
 * <TestSuitesDataGrid
 *   suites={testSuites}
 *   roles={userRoles}
 *   userInfo={currentUser}
 * />
 *
 * @example
 * // With loading state
 * <TestSuitesDataGrid
 *   suites={testSuites}
 *   roles={userRoles}
 *   userInfo={currentUser}
 *   loadingTestSuites={true}
 * />
 *
 * @example
 * // Expected suite data structure
 * const suites = [
 *   {
 *     testsuites: [
 *       {
 *         testsuiteid: 'suite-123',
 *         testsuitename: 'API Integration Tests',
 *         category: 'API',
 *         environment: 'staging',
 *         testcasecount: 25,
 *         role: 'admin',
 *         userlist: { users: [] },
 *         createdby: 'user@example.com',
 *         createddate: '2023-01-01T00:00:00Z',
 *         modifieddate: '2023-01-02T00:00:00Z',
 *         emailrecipients: 'user@example.com',
 *         scheduledtime: '0 8 * * 1 *',
 *         start_date: '2023-01-01T00:00:00Z',
 *         end_date: '2023-12-31T23:59:59Z',
 *         isdisabled: false,
 *         updateXray: true,
 *         jiraProjectId: 'PROJ-123',
 *         jiraTestExecutionId: 'EXEC-456',
 *         lastRun: '06/23/25 - 8:00am',
 *       }
 *     ]
 *   }
 * ];
 *
 * @example
 * // Expected roles data structure
 * const roles = [
 *   { rolename: 'reader', permissions: ['view', 'run'] },
 *   { rolename: 'editor', permissions: ['view', 'edit', 'run'] },
 *   { rolename: 'admin', permissions: ['view', 'edit', 'share', 'delete', 'run', 'schedule'] }
 * ];
 *
 * @example
 * // Expected userInfo data structure
 * const userInfo = {
 *   emailid: 'user@example.com',
 *   username: 'john.doe',
 *   permissions: ['view', 'edit', 'run']
 * };
 */
const TestSuitesDataGrid = ({
  suites,
  roles,
  userInfo,
  loadingTestSuites = false,
}) => {
  // Development-time validation for required props
  if (process.env.NODE_ENV === 'development') {
    if (!suites) {
      console.error('TestSuitesDataGrid: suites prop is required');
    }
    if (!roles) {
      console.error('TestSuitesDataGrid: roles prop is required');
    }
    if (!userInfo) {
      console.error('TestSuitesDataGrid: userInfo prop is required');
    }
  }

  const dispatch = useDispatch();

  // Initialize the new modal system
  const { currentModal, openModal, closeModal, updateModal } = useModal();

  // State for modals and interactions
  const [modalData, setModalData] = useState({ visible: false });
  const [runningTestSuiteIds, setRunningTestSuiteIds] = useState(new Set());
  const [isModalLoading, setIsModalLoading] = useState(false);
  const [openScheduleModal, setOpenScheduleModal] = useState(false);
  const [selectedSuite, setSelectedSuite] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [copiedSuiteId, setCopiedSuiteId] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerTestSuite, setDrawerTestSuite] = useState(null);

  // Helper function to check user permissions
  const userCan = useCallback((action, suiteRole) => {
    const ROLES = {
      reader: ['view', 'run'],
      editor: ['view', 'edit', 'run'],
      admin: ['view', 'edit', 'share', 'delete', 'run', 'schedule'],
      owner: ['view', 'edit', 'share', 'delete', 'run', 'schedule'],
    };

    if (ROLES[suiteRole?.toLowerCase()]) {
      return ROLES[suiteRole.toLowerCase()].includes(action);
    }

    return false;
  }, []);

  // Transform nested suite data into flat rows for DataGrid
  const rows = useMemo(() => {
    if (!suites || suites.length === 0) return [];

    let allRows = suites.reduce((acc, service) => {
      if (service.testsuites) {
        const serviceRows = service.testsuites.map((suite) => ({
          id: suite.testsuiteid,
          testsuiteid: suite.testsuiteid,
          testsuitename: suite.testsuitename,
          category: suite.category,
          environment: suite.environment,
          testcasecount: suite.testcasecount,
          role: suite.role,
          userlist: suite.userlist,
          createdby: suite.createdby,
          createddate: suite.createddate,
          modifieddate: suite.modifieddate,
          emailrecipients: suite.emailrecipients,
          scheduledtime: suite.scheduledtime,
          start_date: suite.start_date,
          end_date: suite.end_date,
          isdisabled: suite.isdisabled,
          updateXray: suite.updateXray,
          jiraProjectId: suite.jiraProjectId,
          jiraTestExecutionId: suite.jiraTestExecutionId,
          // Mock data for Last run - replace with actual data from your API
          lastRun: suite.lastRun || '06/23/25 - 8:00am',
        }));
        return [...acc, ...serviceRows];
      }
      return acc;
    }, []);

    // Filter rows based on search term
    if (searchTerm) {
      allRows = allRows.filter(
        (row) =>
          row.testsuitename.toLowerCase().includes(searchTerm.toLowerCase()) ||
          row.testsuiteid.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    return allRows;
  }, [suites, searchTerm]);

  // Modal handlers
  const toggleModal = useCallback((e) => {
    if (e && e.target?.dataset?.modalName) {
      const { target } = e;
      setModalData({
        categoryid: target.dataset.categoryId,
        name: target.dataset.modalName,
        subcategoryid: target.dataset.subcategoryId,
        testcaseid: target.dataset.testcaseId,
        testsuiteid: target.dataset.testsuiteid,
        emailrecipients: target.dataset.emailrecipients,
        userlist: target.dataset.userlist,
        visible: true,
        executionName: '',
      });
    } else {
      setModalData({ visible: false });
      setSelectedSuite(null); // Clear selectedSuite when closing modals
    }
  }, []);

  // Handler for running test suite using new modal system
  const handleRunSuiteStart = useCallback((testSuiteId) => {
    setRunningTestSuiteIds((prev) => new Set([...prev, testSuiteId]));
  }, []);

  const handleRunSuiteComplete = useCallback(
    (testSuiteId) => {
      setRunningTestSuiteIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(testSuiteId);
        return newSet;
      });
      closeModal();
    },
    [closeModal]
  );

  const handleDeleteTestSuite = useCallback(() => {
    setIsModalLoading(true);
    dispatch(removeTestSuite(selectedSuite.testsuiteid)).then(() => {
      setIsModalLoading(false);
      toggleModal();
    });
  }, [selectedSuite, dispatch, toggleModal]);

  const onCopyTestSuiteId = useCallback((suite) => {
    navigator.clipboard.writeText(suite.testsuiteid);
    setCopiedSuiteId(suite.testsuiteid);
    // Reset the copied state after 2 seconds
    setTimeout(() => {
      setCopiedSuiteId(null);
    }, 2000);
  }, []);

  // Drawer handlers
  const handleOpenRunModal = useCallback(
    (suite) => {
      setSelectedSuite(suite);
      openModal(MODAL_IDS.RUN_SUITE, {
        testSuite: suite,
        callbacks: {
          onRunSuiteStart: handleRunSuiteStart,
          onRunSuiteComplete: handleRunSuiteComplete,
        },
      });
    },
    [openModal, handleRunSuiteStart, handleRunSuiteComplete]
  );

  const handleRowClick = useCallback((params, event) => {
    // Prevent opening drawer if clicking on action buttons
    if (
      event.target.closest('.run-button') ||
      event.target.closest('.MuiButton-root')
    ) {
      return;
    }
    setDrawerTestSuite(params.row);
    setDrawerOpen(true);
  }, []);

  const handleDrawerClose = useCallback(() => {
    setDrawerOpen(false);
    setDrawerTestSuite(null);
  }, []);

  // Share handler for drawer using new modal system
  const handleShareFromDrawer = useCallback(
    (testSuite) => {
      // Close drawer first
      setDrawerOpen(false);
      setDrawerTestSuite(null);

      // Open share modal using new system
      openModal(MODAL_IDS.SHARE_SUITE, {
        testSuite: {
          testsuiteid: testSuite.testsuiteid,
          testsuitename: testSuite.testsuitename,
          userlist: testSuite.userlist || { users: [] },
          createdby: testSuite.createdby, // Pass owner email to prevent them from being in shared list
        },
        sharedUsers:
          testSuite.userlist?.users?.map((user) => ({
            email: user.user,
            role: user.role,
          })) || [],
        emailInput: '',
        selectedRole: roles.length > 0 ? roles[0].rolename : '',
      });
    },
    [openModal, roles]
  );

  // Run handler for drawer using new modal system
  const handleRunFromDrawer = useCallback(
    (testSuite) => {
      // Close drawer first
      setDrawerOpen(false);
      setDrawerTestSuite(null);

      handleOpenRunModal(testSuite);
    },
    [handleOpenRunModal]
  );

  // Modal submission handlers using new system
  const handleModalSubmit = useCallback(
    (modalId, formData) => {
      switch (modalId) {
        case MODAL_IDS.SHARE_SUITE:
          // Return the promise directly so errors can be caught by the modal
          return dispatch(
            shareTestSuite(formData.testSuiteId, formData.sharedUsers)
          );

        case MODAL_IDS.RUN_SUITE:
          // handled directly in modal; no additional action needed here
          return Promise.resolve();

        default:
          console.warn(`No handler found for modal: ${modalId}`);
          return Promise.resolve();
      }
    },
    [dispatch]
  );

  // Column definitions matching Figma design
  const columns = useMemo(
    () => [
      {
        field: 'testsuitename',
        headerName: 'Suite name',
        flex: 1,
        minWidth: 200,
        renderCell: (params) => {
          const suite = params.row;

          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {suite.testsuitename}
              </Typography>

              <Tooltip
                title={
                  copiedSuiteId === suite.testsuiteid
                    ? 'Copied!'
                    : 'Copy test suite ID'
                }
              >
                <IconButton
                  size="small"
                  onClick={() => onCopyTestSuiteId(suite)}
                  sx={{
                    padding: '4px',
                    minWidth: 'auto',
                    minHeight: 'auto',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                    lineHeight: 0,
                  }}
                >
                  <CopyIcon
                    sx={{
                      color:
                        copiedSuiteId === suite.testsuiteid
                          ? 'success.main'
                          : 'text.secondary',
                      transition: 'color 0.2s ease-in-out',
                    }}
                  />
                </IconButton>
              </Tooltip>
            </Box>
          );
        },
      },
      {
        field: 'category',
        headerName: 'Category',
        flex: 0.4,
        minWidth: 100,
        renderCell: (params) => (
          <Typography variant="body2" color="text.secondary">
            {params.value}
          </Typography>
        ),
      },
      {
        field: 'environment',
        headerName: 'Environment',
        flex: 0.5,
        minWidth: 120,
        renderCell: (params) => (
          <Typography variant="body2" color="text.secondary">
            {params.value}
          </Typography>
        ),
      },
      {
        field: 'lastRun',
        headerName: 'Last run',
        flex: 0.6,
        minWidth: 140,
        renderCell: (params) => (
          <Typography variant="body2" color="text.secondary">
            {params.value}
          </Typography>
        ),
      },
      {
        field: 'schedule',
        headerName: 'Schedule',
        flex: 0.8,
        minWidth: 220,
        renderCell: (params) => {
          const suite = params.row;
          return (
            <ScheduleDisplay
              scheduledtime={suite.scheduledtime}
              start_date={suite.start_date}
              end_date={suite.end_date}
              showTime={true}
              orientation="horizontal"
              size="medium"
            />
          );
        },
      },
      {
        field: 'testcasecount',
        headerName: 'Tests',
        flex: 0.3,
        minWidth: 80,
        type: 'number',
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => (
          <Typography variant="body2" fontWeight={500}>
            {params.value}
          </Typography>
        ),
      },
      {
        field: 'actions',
        headerName: 'Actions',
        flex: 0.6,
        minWidth: 140,
        sortable: false,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => {
          const suite = params.row;

          return (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                height: '100%',
              }}
            >
              {userCan('run', suite.role) && (
                <Link
                  component="button"
                  variant="body2"
                  className="run-button"
                  sx={{
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    color: 'text.primary',
                    fontSize: '16px ',
                    fontWeight: 500,
                    border: 'none',
                    background: 'none',
                    padding: 0,
                    '&:hover': {
                      color: 'text.secondary',
                      textDecoration: 'underline',
                    },
                    '&:disabled': {
                      color: 'text.disabled',
                      cursor: 'not-allowed',
                      textDecoration: 'none',
                    },
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenRunModal(suite);
                  }}
                  disabled={runningTestSuiteIds.has(suite.testsuiteid)}
                >
                  {runningTestSuiteIds.has(suite.testsuiteid)
                    ? 'Running'
                    : 'Run'}
                </Link>
              )}
            </Box>
          );
        },
      },
    ],
    [
      userCan,
      onCopyTestSuiteId,
      runningTestSuiteIds,
      handleOpenRunModal,
      copiedSuiteId,
    ]
  );

  // Empty state component
  const EmptyState = useCallback(
    () => (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 200,
          width: '100%',
          marginTop: '80px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            width: '664px',
            padding: '32px',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            border: '1px solid #CCC',
            background: '#FFF',
            height: '167px',
          }}
        >
          <Box sx={{ alignSelf: 'flex-start', mb: '0px' }}>
            <Typography
              sx={{
                fontSize: '20px',
                fontWeight: 700,
                color: '#000000',
                mb: '0px',
                textAlign: 'left',
              }}
            >
              You don't have any test suites yet.
            </Typography>
            <Typography
              sx={{
                fontSize: '14px',
                color: '#000000',
                textAlign: 'left',
              }}
            >
              Create your first one to group and run tests with a single click.
            </Typography>
          </Box>
          <Button
            href="/testsuites/new"
            variant={buttonVariants.CONTAINED}
            target={buttonTargets.SELF}
            startIcon={<AddIcon />}
            sx={{
              backgroundColor: '#000000 !important',
              color: '#ffffff !important',
              '&:hover': {
                backgroundColor: '#333333 !important',
              },
              '&.MuiButton-contained': {
                backgroundColor: '#000000 !important',
                color: '#ffffff !important',
              },
              '&.MuiButton-contained:hover': {
                backgroundColor: '#333333 !important',
              },
              fontSize: '14px',
              lineHeight: '18px',
              padding: '4px 12px',
              borderRadius: '4px',
              width: '186px',
              height: '32px',
              '& .MuiButton-startIcon': {
                margin: '0 !important',
              },
              marginTop: '24px',
            }}
          >
            Create a test suite
          </Button>
        </Box>
      </Box>
    ),
    []
  );

  // Loading state component
  const LoadingState = useCallback(
    () => (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: 200,
        }}
      >
        <LoadingSpinner text="Loading test suites" />
      </Box>
    ),
    []
  );

  // Modal rendering
  const renderModal = useCallback(() => {
    if (!modalData.visible) return null;

    let modalProps = {
      onClose: toggleModal,
    };

    switch (modalData.name) {
      case 'share':
        return (
          <ShareTestSuiteModal
            open={true}
            onClose={toggleModal}
            userInfo={userInfo}
            roles={roles.filter((r) => r.rolename !== 'Owner')}
            testsuiteid={modalData.testsuiteid}
            testSuiteName={selectedSuite?.testsuitename}
            createdBy={selectedSuite?.createdby}
            userlist={
              modalData.userlist && modalData.userlist !== 'undefined'
                ? JSON.parse(modalData.userlist)
                : { users: [] }
            }
          />
        );

      case 'delete-testsuite':
        return (
          <ConfirmationModal
            title="Remove test suite"
            message="Proceeding will remove the selected test suite. This action cannot be undone."
            submitButtonText="Remove test suite"
            onConfirm={handleDeleteTestSuite}
            loading={isModalLoading}
            onClose={toggleModal}
          />
        );

      default:
        return null;
    }
  }, [
    modalData,
    toggleModal,
    selectedSuite,
    userInfo,
    roles,
    handleDeleteTestSuite,
    isModalLoading,
  ]);

  if (loadingTestSuites) {
    return <LoadingState />;
  }

  // Show EmptyState only when there are no test suites at all (not filtered)
  const hasNoTestSuites =
    !suites ||
    suites.length === 0 ||
    suites.every(
      (service) => !service.testsuites || service.testsuites.length === 0
    );

  if (hasNoTestSuites) {
    return <EmptyState />;
  }

  return (
    <Box sx={{ width: '100%' }}>
      {/* Search Bar */}
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          variant="filled"
          placeholder="Search by test suite ID or name"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            disableUnderline: true,
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon
                  sx={{ color: 'text.secondary', marginLeft: '8px' }}
                />
              </InputAdornment>
            ),
          }}
          sx={{
            maxWidth: 400,
            '& .MuiFilledInput-root': {
              backgroundColor: '#f5f5f5 !important',
              border: 'none !important',
              borderBottom: 'none !important',
              '&:before': {
                borderBottom: 'none !important',
              },
              '&:after': {
                borderBottom: 'none !important',
              },
              '&:hover:not(.Mui-disabled):before': {
                borderBottom: 'none !important',
              },
              '&.Mui-focused:after': {
                borderBottom: 'none !important',
              },
            },
            '& .MuiInputBase-root': {
              backgroundColor: '#f5f5f5 !important',
              border: 'none !important',
              borderBottom: 'none !important',
              minHeight: '36px !important',
              '&:before': {
                display: 'none !important',
              },
              '&:after': {
                display: 'none !important',
              },
            },
            '& .MuiFormControl-root .MuiInputBase-root': {
              minHeight: '36px !important',
            },
            '& .MuiInputBase-input': {
              padding: '8px 0 8px !important',
              backgroundColor: 'transparent !important',
            },
            '& .MuiInputAdornment-root': {
              marginTop: '0px !important',
            },
            '& .MuiFormControl-root-MuiTextField-root': {
              minHeight: '36px !important',
            },
          }}
        />
      </Box>

      {/* Data Grid */}
      <DataGrid
        rows={rows}
        columns={columns}
        pagination
        hideFooter={false}
        hideFooterPagination={false}
        initialState={{
          pagination: {
            paginationModel: { pageSize: 10 },
          },
        }}
        rowHeight={48}
        headerHeight={48}
        pageSizeOptions={[10, 25, 50, 100]}
        checkboxSelection={false}
        disableSelectionOnClick
        sortingOrder={['asc', 'desc']}
        onRowClick={handleRowClick}
        slots={{
          noRowsOverlay: () => (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
                minHeight: 200,
                flexDirection: 'column',
                gap: 1,
              }}
            >
              <Typography variant="h6" color="text.secondary">
                No test suites found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {searchTerm
                  ? `No test suites match "${searchTerm}". Try adjusting your search.`
                  : 'No test suites available to display.'}
              </Typography>
            </Box>
          ),
        }}
        sx={{
          '& .MuiDataGrid-root': {
            border: 'none',
          },
          '& .MuiDataGrid-cell': {
            borderRight: 'none !important',
            borderLeft: 'none !important',
            borderTop: 'none !important',
            fontSize: '0.875rem',
            padding: '8px 16px',
            display: 'flex',
            alignItems: 'center',
          },
          '& .MuiDataGrid-row .MuiDataGrid-cell': {
            borderRight: 'none !important',
            borderLeft: 'none !important',
            borderTop: 'none !important',
          },
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: '#ffffff !important',
            borderBottom: '1px solid',
            borderColor: 'divider',
            fontWeight: 600,
            fontSize: '16px',
            lineHeight: '24px',
            color: 'text.secondary',
            '& .MuiDataGrid-columnHeader': {
              padding: '16px',
              borderRight: 'none !important',
              borderLeft: 'none !important',
              backgroundColor: '#ffffff !important',
            },
            '& .MuiDataGrid-columnSeparator': {
              display: 'none !important',
            },
          },
          '& .MuiDataGrid-columnHeader': {
            borderRight: 'none !important',
            borderLeft: 'none !important',
            backgroundColor: '#ffffff !important',
          },
          '& .MuiDataGrid-columnSeparator': {
            display: 'none !important',
          },
          '& .MuiDataGrid-virtualScroller': {
            '& .MuiDataGrid-cell': {
              borderRight: 'none !important',
              borderLeft: 'none !important',
            },
          },
          '& .MuiDataGrid-cellRight': {
            borderRight: 'none !important',
            borderLeft: 'none !important',
          },
          '& .MuiDataGrid-cellCenter': {
            borderRight: 'none !important',
            borderLeft: 'none !important',
          },
          '& .MuiDataGrid-cellLeft': {
            borderRight: 'none !important',
            borderLeft: 'none !important',
          },
          '& .MuiDataGrid-cell:not(.MuiDataGrid-cellEmpty)::after': {
            borderRight: 'none !important',
            content: 'none !important',
            display: 'none !important',
          },
          '& .MuiDataGrid-row': {
            borderBottom: '1px solid',
            borderColor: 'divider',
            cursor: 'pointer !important',
            '&:hover': {
              backgroundColor: 'grey.50',
              cursor: 'pointer !important',
            },
            '&:last-child': {
              borderBottom: 'none',
            },
          },
          '& .MuiDataGrid-footerContainer': {
            borderTop: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'background.paper',
            backgroundImage: 'none',
            justifyContent: 'space-between',
            padding: '16px',
          },
          '& .MuiTablePagination-root': {
            fontSize: '0.875rem',
            color: 'text.secondary',
            backgroundColor: 'background.paper',
          },
          '& .MuiTablePagination-displayedRows': {
            fontSize: '0.875rem',
            color: 'text.secondary',
          },
          '& .MuiDataGrid-footerContainer .MuiTablePagination-input': {
            borderBottom: 'none',
            boxShadow: 'none',
            '&:hover': {
              boxShadow: 'none',
            },
            '&:focus': {
              boxShadow: 'none',
            },
            '&.Mui-focused': {
              borderBottom: 'none',
              boxShadow: 'none',
            },
          },
          '& .MuiDataGrid-selectedRowCount': {
            visibility: 'hidden',
          },
        }}
      />

      {renderModal()}

      {/* New Modal System Renderer */}
      <ModalRenderer
        currentModal={currentModal}
        onClose={closeModal}
        onSubmit={handleModalSubmit}
        onUpdate={updateModal}
        userInfo={userInfo}
        roles={roles.filter((r) => r.rolename !== 'Owner')}
      />

      {openScheduleModal && selectedSuite && (
        <ScheduleModal
          open={openScheduleModal}
          onClose={() => {
            setOpenScheduleModal(false);
            setSelectedSuite(null);
          }}
          testSuiteId={selectedSuite.testsuiteid}
          schedule={{
            disabled: selectedSuite.isdisabled,
            cronExpression: selectedSuite.scheduledtime,
            dateStart: selectedSuite.start_date,
            dateEnd: selectedSuite.end_date,
            updateXray: selectedSuite.updateXray,
            jiraProjectId: selectedSuite.jiraProjectId,
            jiraTestExecutionId: selectedSuite.jiraTestExecutionId,
          }}
        />
      )}

      <TestSuiteDetailsDrawer
        open={drawerOpen}
        onClose={handleDrawerClose}
        onShare={handleShareFromDrawer}
        onRun={handleRunFromDrawer}
        testSuite={drawerTestSuite}
      />
    </Box>
  );
};

export default TestSuitesDataGrid;
